/** Code-based TanStack Router tree (BUILD_PLAN section 6, ADMIN_UI_SPEC). */
import { Outlet, createRootRoute, createRoute, createRouter } from '@tanstack/react-router';
import type { FunctionComponent } from 'react';
import { z } from 'zod';
import { RequireAuth } from './app/RequireAuth.js';
import { Shell } from './app/Shell.js';
import { GalleryPage } from './routes/gallery.js';
import { LoginPage } from './pages/Login.js';
import { OverviewPage } from './pages/Overview.js';
import { PeopleListPage } from './pages/people/PeopleList.js';
import { PersonDetailPage } from './pages/people/PersonDetail.js';
import { InvitationsPage } from './pages/people/Invitations.js';
import { SessionsPage } from './pages/people/Sessions.js';
import { AccountsListPage } from './pages/accounts/AccountsList.js';
import { AccountDetailPage } from './pages/accounts/AccountDetail.js';
import { ConnectionsPage } from './pages/accounts/Connections.js';
import { DevicesPage } from './pages/Devices.js';
import { ApiKeysPage } from './pages/apiKeys/ApiKeys.js';
import { ContentPage } from './pages/templates/Content.js';
import { TemplatesPage } from './pages/templates/Templates.js';
import { TemplateDetailPage } from './pages/templates/TemplateDetail.js';
import { ReleasesPage } from './pages/templates/Releases.js';
import { AiUsagePage, UsagePage } from './pages/usage/Usage.js';
import { AuditExplorerPage } from './pages/audit/AuditExplorer.js';
import { AuditEventPage } from './pages/audit/AuditEvent.js';
import { ExportsPage } from './pages/audit/Exports.js';
import { OutboxPage } from './pages/ops/Outbox.js';
import { MailPage } from './pages/ops/MailPage.js';
import { ReleasePolicyPage } from './pages/ops/ReleasePolicy.js';
import { RateLimitsPage } from './pages/ops/RateLimits.js';
import { FeatureFlagsPage } from './pages/ops/FeatureFlags.js';
import { ModelConfigPage } from './pages/ops/ModelConfig.js';
import { LeasesPage } from './pages/ops/Leases.js';
import { HealthPage } from './pages/ops/Health.js';
import { IntegrationsPage } from './pages/ops/Integrations.js';

export const rootRoute = createRootRoute({ component: () => <Outlet /> });

const loginSearchSchema = z.object({ returnTo: z.string().optional(), error: z.string().optional() });

export const loginRoute = createRoute({ getParentRoute: () => rootRoute, path: '/login', component: LoginPage, validateSearch: loginSearchSchema });

export const galleryRoute = createRoute({ getParentRoute: () => rootRoute, path: '/dev/gallery', component: GalleryPage });

/** Authenticated shell: rail + top bar + content. */
export const shellRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: 'shell',
  component: () => (
    <RequireAuth>
      <Shell />
    </RequireAuth>
  ),
});

const page = <P extends string>(path: P, component: FunctionComponent) => createRoute({ getParentRoute: () => shellRoute, path, component });

export const indexRoute = page('/', OverviewPage);
export const peopleRoute = page('/people', PeopleListPage);
export const personRoute = page('/people/$userId', PersonDetailPage);
export const invitationsRoute = page('/invitations', InvitationsPage);
export const sessionsRoute = page('/sessions', SessionsPage);
export const accountsRoute = page('/accounts', AccountsListPage);
export const accountRoute = page('/accounts/$accountId', AccountDetailPage);
export const connectionsRoute = page('/connections', ConnectionsPage);
export const devicesRoute = page('/devices', DevicesPage);
export const apiKeysRoute = page('/api-keys', ApiKeysPage);
export const contentRoute = page('/content', ContentPage);
export const templatesRoute = createRoute({ getParentRoute: () => shellRoute, path: '/templates', component: TemplatesPage, validateSearch: z.object({ type: z.string().optional(), state: z.string().optional() }) });
export const templateRoute = page('/templates/$templateId', TemplateDetailPage);
export const releasesRoute = page('/releases', ReleasesPage);
export const usageRoute = page('/usage', UsagePage);
export const aiUsageRoute = page('/usage/ai', AiUsagePage);
export const auditRoute = page('/audit', AuditExplorerPage);
export const auditEventRoute = page('/audit/$eventId', AuditEventPage);
export const exportsRoute = page('/exports', ExportsPage);
export const outboxRoute = page('/ops/outbox', OutboxPage);
export const mailRoute = page('/ops/mail', MailPage);
export const releasePolicyRoute = page('/ops/release-policy', ReleasePolicyPage);
export const rateLimitsRoute = page('/ops/rate-limits', RateLimitsPage);
export const featureFlagsRoute = page('/ops/feature-flags', FeatureFlagsPage);
export const modelConfigRoute = page('/ops/model-config', ModelConfigPage);
export const leasesRoute = page('/ops/leases', LeasesPage);
export const healthRoute = page('/ops/health', HealthPage);
export const integrationsRoute = page('/ops/integrations', IntegrationsPage);

// The component gallery is a development aid; it is not part of a production bundle.
const devRoutes = import.meta.env.DEV ? [galleryRoute] : [];

const routeTree = rootRoute.addChildren([
  loginRoute,
  ...devRoutes,
  shellRoute.addChildren([
    indexRoute,
    peopleRoute,
    personRoute,
    invitationsRoute,
    sessionsRoute,
    accountsRoute,
    accountRoute,
    connectionsRoute,
    devicesRoute,
    apiKeysRoute,
    contentRoute,
    templatesRoute,
    templateRoute,
    releasesRoute,
    usageRoute,
    aiUsageRoute,
    auditRoute,
    auditEventRoute,
    exportsRoute,
    outboxRoute,
    mailRoute,
    releasePolicyRoute,
    rateLimitsRoute,
    featureFlagsRoute,
    modelConfigRoute,
    leasesRoute,
    healthRoute,
    integrationsRoute,
  ]),
]);

export const router = createRouter({ routeTree, defaultPreload: 'intent', scrollRestoration: true });

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}
